'use client'

import { Dialog } from '@vinted/web-ui'
import { useEffect } from 'react'

import { useConversationContext } from '@marketplace-web/domain/core-conversation'
import { useFetch } from '@marketplace-web/shared/api-client'
import { useTranslate } from '@marketplace-web/shared/i18n'
import { ContentLoader } from '@marketplace-web/shared/ui-helpers'
import {
  ComplaintResolutionAction,
  getComplaint,
  transformSnadComplaintDto,
} from '@marketplace-web/domain/complaints-data'

type Props = {
  isOpen: boolean
  onSubmit: () => void
  onClose: () => void
}

const ResolveIssueModal = ({ isOpen, onSubmit, onClose }: Props) => {
  const translate = useTranslate('conversation.actions.resolve_complaint.modal')
  const { transaction } = useConversationContext()
  const complaintId = transaction?.complaintId

  const {
    fetch: fetchComplaint,
    transformedData: complaint,
    isLoading,
  } = useFetch(getComplaint, transformSnadComplaintDto)

  const resolutionAction = complaint?.resolutions?.find(
    resolution => resolution.action === ComplaintResolutionAction.Refund,
  )

  useEffect(() => {
    if (!complaintId || !isOpen) return

    fetchComplaint(complaintId)
  }, [fetchComplaint, complaintId, isOpen])

  if (isLoading) {
    return (
      <Dialog show>
        <ContentLoader testId="resolve-issue-modal-loader" />
      </Dialog>
    )
  }

  if (!resolutionAction) return null

  return (
    <Dialog
      show={isOpen}
      title={resolutionAction?.confirmTitle}
      body={resolutionAction?.confirmation}
      actions={[
        {
          text: resolutionAction?.confirmButton,
          style: Dialog.ActionStyling.Filled,
          callback: onSubmit,
          testId: 'resolve-issue-modal--submit-button',
        },
        {
          text: translate('cancel'),
          style: Dialog.ActionStyling.Flat,
          callback: onClose,
        },
      ]}
    />
  )
}

export default ResolveIssueModal
